import { useMutation, useQuery, useQueryClient } from "react-query";
import { api } from "../../../_metronic/helpers/api";
import { getAuth } from "../auth";
import axios from "axios";

export interface OrderItem {
  produto_id: string;
  produto_nome: string;
  quantidade: number;
  sub_total: number;
  observacao: string;
  valor_unitario: number;
}

export interface Pagamento {
  user_id: number;
  payment_status: string;
  billing_type: string;
  due_date: string;
  value: number;
  invoice_url: string;
  order_id: number;
  caixa_id: string;
  valor_recebido: number;
  valor_troco: number;
}

export type OrderResponse = {
  id: number;
  clube: number;
  clube_local_venda_id: string;
  clube_local_venda_nome: string;
  clube_local_mesa_id: string;
  clube_local_mesa_nome: string;
  user_id_react: number;
  pedido_uniqueid: string;
  status_pedido: string;
  valor_total_pedido: number;
  itens: OrderItem[];
  user_id_bubble: string;
  user_email: string;
  data: string;
  hora: string;
  data_hora_unix: number;
  vendedor_id_bubble: string;
  vendedor_email: string;
  caixa_id: string;
  pagamentos: Pagamento[];
};

/** ID do cliente vinculado ao pedido (react id, pagamento ou e-mail). */
export function getOrderClientUserId(order: OrderResponse): number {
  const fromReact = Number(order.user_id_react || 0);
  if (fromReact > 0) return fromReact;
  const fromPay = Number(order.pagamentos?.[0]?.user_id || 0);
  if (fromPay > 0) return fromPay;
  return 0;
}

/** Localiza o cadastro do cliente do pedido (por id ou e-mail). */
export function findUserForOrder(
  order: OrderResponse,
  users: any[] | undefined | null,
): any | null {
  const list = Array.isArray(users) ? users : [];
  const clientId = getOrderClientUserId(order);
  if (clientId > 0) {
    const byId = list.find((u) => Number(u?._id ?? u?.id ?? 0) === clientId);
    if (byId) return byId;
  }
  const email = String(order.user_email || "")
    .trim()
    .toLowerCase();
  if (email) {
    const byEmail = list.find(
      (u) => String(u?.email || "").trim().toLowerCase() === email,
    );
    if (byEmail) return byEmail;
  }
  return null;
}

export function formatUserFullName(user: any | null | undefined): string {
  if (!user) return "";
  const nome = String(user.nome || user.first_name || "").trim();
  const sobrenome = String(user.sobrenome || user.last_name || "").trim();
  const name = [nome, sobrenome].filter(Boolean).join(" ");
  if (name) return name;
  if (user.email) return String(user.email);
  return "";
}

/** Nome exibido: `u.nome` + espaço + `u.sobrenome` (companies-users.usuarios). */
export function formatUsuarioNomeSobrenome(
  nome?: string | null,
  sobrenome?: string | null,
): string {
  return [String(nome || "").trim(), String(sobrenome || "").trim()]
    .filter(Boolean)
    .join(" ");
}

export function getOrderClientName(
  order: OrderResponse,
  users: any[] | undefined | null,
  extraUser?: any | null,
): string {
  const user = extraUser || findUserForOrder(order, users);
  const fromUser = formatUserFullName(user);
  if (fromUser) return fromUser;
  const email = String(order.user_email || "").trim();
  if (email && !email.includes("@")) return email;
  if (email.includes("@")) {
    const local = email.split("@")[0];
    if (local) return local;
  }
  return "";
}

const getOrders = async () => {
  return await api.get<OrderResponse[]>("/orders");
};

const updateStatusOrder = async (
  orderToUpdate: OrderResponse,
  novoStatus: string,
) => {
  return await api.post(`/orders/status/${orderToUpdate.id}`, {
    status: novoStatus,
  });
};

const getUsersCadastrados = async () => {
  return await api.get(`topbar/report?category=registeredUsers`);
};

/** Busca cadastro do cliente por id (quando não veio na lista do topbar). */
export async function fetchUserById(userId: number): Promise<any | null> {
  if (!userId) return null;
  const auth = getAuth();
  const apiUrl = process.env.REACT_APP_API_URL || "";
  try {
    const response = await axios.get(`${apiUrl}/users/address/${userId}`, {
      headers: {
        Authorization: `Bearer ${auth?.api_token}`,
        operation: "users/find-by-id",
      },
    });
    const data = response.data || null;
    if (!data) return null;
    return {
      ...data,
      _id: data._id ?? data.id ?? userId,
    };
  } catch {
    return null;
  }
}

export const useOrders = () => {
  const queryClient = useQueryClient();

  const { data, isLoading, isError } = useQuery(["orders"], () => getOrders(), {
    refetchOnWindowFocus: false,
    refetchOnMount: false,
    refetchInterval: 1000 * 60 * 5,
  });

  const {
    data: userData,
    isLoading: loadinUser,
    isError: errorUser,
  } = useQuery(["users"], () => getUsersCadastrados(), {
    refetchOnWindowFocus: false,
    refetchOnMount: false,
    refetchInterval: 1000 * 60 * 5,
  });

  const updateStatusMutation = useMutation(
    ({ order, status }: { order: OrderResponse; status: string }) =>
      updateStatusOrder(order, status),
    {
      onMutate: async () => {
        await queryClient.cancelQueries("orders");
      },
      onSettled: () => {
        queryClient.invalidateQueries("orders");
      },
    },
  );

  const refetch = async () => {
    await queryClient.invalidateQueries("orders");
  };

  return {
    orders: data?.data ?? [],
    isLoading,
    isError,
    updateStatusMutation,
    userData: userData?.data ? userData?.data?.users : [],
    loadinUser,
    errorUser,
    refetch,
  };
};
